import {
  ApiErrorText,
  Button,
  CopyField,
  InstructionsList,
} from '@/shared/components';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useDepositMethodData } from '../api/useDepositMethodData';
import { renderIcon } from '../lib/iconRenderer';

type DepositMethod = 'oplati' | 'erip';

type DepositInstructionIcon =
  | ComponentType<SVGProps<SVGSVGElement>>
  | ReactNode
  | string;

interface DepositInstructionContentProps {
  depositMethod: DepositMethod;
  icon: DepositInstructionIcon;
}

export function DepositInstructionContent({
  depositMethod,
  icon,
}: DepositInstructionContentProps) {
  const { t } = useTranslation();
  const {
    data: depositData,
    isError,
    error,
    isLoading,
  } = useDepositMethodData(depositMethod);

  if (isLoading) {
    return <></>;
  }

  if (isError || !depositData) {
    return (
      <ApiErrorText
        error={error}
        size="small"
        className="text-center p-[4rem]"
      />
    );
  }

  return (
    <div className="flex flex-col items-center space-y-[1.6rem]">
      {icon && (
        <div className="flex items-center justify-center w-[14.4rem] h-[14.4rem]">
          {renderIcon({
            icon,
            alt: depositMethod,
          })}
        </div>
      )}

      <InstructionsList
        titleKey={`depositMethods.${depositMethod}.instructionsTitle`}
        simpleStepsKey={`depositMethods.${depositMethod}.simpleSteps`}
        numberedStepsKey={`depositMethods.${depositMethod}.numberedSteps`}
      />

      <CopyField
        className="w-full"
        label={t(`depositMethods.${depositMethod}.accountNumber`)}
        value={depositData.accountNumber}
        qrCodeData={depositData.qrCode}
      />

      {depositData.qrCode && (
        <>
          <p className="p3 text-muted-foreground flex justify-center">
            {t('common.or')}
          </p>

          <Button
            className="w-full rounded-[1.2rem] px-[3.2rem] py-[1.6rem] headline4"
            asChild>
            <a
              href={depositData.qrCode}
              target="_blank"
              rel="noopener noreferrer">
              {t('depositMethods.payByLink')}
            </a>
          </Button>
        </>
      )}
    </div>
  );
}
